import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import CertificateCard from "@/components/CertificateCard";
import {
  Trophy, Loader2, ScanText, Crown, Flame, Award, CheckCircle2, Circle, CalendarDays,
  CalendarRange, Star, GraduationCap, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function rewardText(r) {
  if (!r) return "";
  const parts = [];
  if (r.xp) parts.push(`+${r.xp} XP`);
  if (r.credits) parts.push(`+${r.credits} credits`);
  if (r.badge) parts.push(`${r.badge.name} badge`);
  if (r.trial_token) parts.push(`+${r.trial_token.n} ${r.trial_token.type} token`);
  return parts.join(" · ");
}

const ChallengeCard = ({ ch, onClaim, claiming, onDecode }) => {
  const pct = Math.round((ch.progress / Math.max(1, ch.target)) * 100);
  return (
    <div className="litra-card p-4 flex flex-col" data-testid={`challenge-card-${ch.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-semibold text-sm">{ch.title}</div>
          <div className="text-xs text-[var(--bone-faint)] mt-0.5">{ch.description}</div>
        </div>
        {ch.theme && <Badge variant="outline" className="text-[10px] shrink-0">{ch.theme}</Badge>}
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-[var(--bone-faint)]">{ch.progress} / {ch.target}</span>
          {ch.completed && !ch.claimed && <span className="green-text">Ready</span>}
        </div>
        <Progress value={pct} className="h-1.5 bg-white/[0.06]" data-testid={`challenge-progress-${ch.id}`} />
      </div>
      <div className="mt-2 text-[11px] text-[var(--bone-faint)]">Reward: <span className="gold-text">{rewardText(ch.reward)}</span></div>
      <div className="mt-3 mt-auto pt-2">
        {ch.claimed ? (
          <div className="flex items-center gap-1.5 text-xs green-text" data-testid={`challenge-claimed-${ch.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Claimed</div>
        ) : ch.completed ? (
          <Button size="sm" onClick={() => onClaim(ch)} disabled={claiming} className="w-full gold-edge gold-text bg-transparent hover:bg-[rgba(176,141,60,0.14)]" data-testid={`challenge-claim-${ch.id}`}>
            {claiming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Award className="w-3.5 h-3.5 mr-1.5" /> Claim</>}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onDecode} className="w-full glass" data-testid={`challenge-go-${ch.id}`}>
            <ScanText className="w-3.5 h-3.5 mr-1.5" /> {ch.metric === "lessons" ? "Open Doctrine" : ch.metric === "studio" ? "Open Studio" : "Decode"}
          </Button>
        )}
      </div>
    </div>
  );
};

const ProgramCard = ({ p, onClaim, claiming }) => (
  <div className="litra-card p-5" style={p.earned && !p.claimed ? { borderColor: "rgba(176,141,60,0.4)" } : {}} data-testid={`program-card-${p.code}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4 gold-text" /><span className="font-display font-semibold">{p.title}</span></div>
        <p className="text-xs text-[var(--bone-faint)] mt-1">{p.description}</p>
      </div>
      {p.claimed && <Badge className="gold-edge gold-text bg-transparent text-[10px] shrink-0">Earned</Badge>}
    </div>
    <div className="mt-3 space-y-1.5">
      {p.requirements.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-xs" data-testid={`program-req-${p.code}-${r.key}`}>
          {r.done ? <CheckCircle2 className="w-3.5 h-3.5 green-text shrink-0" /> : <Circle className="w-3.5 h-3.5 text-[var(--bone-faint)] shrink-0" />}
          <span className={r.done ? "text-[var(--bone)]" : "text-[var(--bone-faint)]"}>{r.label}</span>
          {!r.done && r.target > 1 && <span className="text-[var(--bone-faint)] ml-auto">{r.progress}/{r.target}</span>}
        </div>
      ))}
    </div>
    <div className="mt-3 text-[11px] text-[var(--bone-faint)]">Reward: <span className="gold-text">{rewardText(p.reward)}</span></div>
    <div className="mt-3">
      {p.claimed ? (
        <div className="flex items-center gap-1.5 text-xs green-text"><CheckCircle2 className="w-3.5 h-3.5" /> Certificate earned</div>
      ) : (
        <Button size="sm" onClick={() => onClaim(p)} disabled={!p.earned || claiming} className="w-full gold-edge gold-text bg-transparent hover:bg-[rgba(176,141,60,0.14)] disabled:opacity-40" data-testid={`program-claim-${p.code}`}>
          {claiming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : p.earned ? <><Award className="w-3.5 h-3.5 mr-1.5" /> Claim Certificate</> : "Requirements not met"}
        </Button>
      )}
    </div>
  </div>
);

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 green-text" />
      <h3 className="font-display font-semibold">{title}</h3>
      {subtitle && <span className="mono-chip text-[var(--bone-faint)]">{subtitle}</span>}
    </div>
    {children}
  </div>
);

export default function Challenges() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [overview, setOverview] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [board, setBoard] = useState(null);
  const [certs, setCerts] = useState(null);
  const [claiming, setClaiming] = useState("");

  const loadChallenges = useCallback(async () => {
    try { const r = await api.get("/challenges/overview"); setOverview(r.data); } catch (e) { /* ignore */ }
  }, []);
  const loadPrograms = useCallback(async () => {
    try { const r = await api.get("/certificates/programs"); setPrograms(r.data); } catch (e) { /* ignore */ }
  }, []);
  const loadExtras = useCallback(async () => {
    try { const r = await api.get("/leaderboards"); setBoard(r.data); } catch (e) { /* ignore */ }
    try { const r = await api.get("/certificates/evaluate"); setCerts(r.data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadChallenges(); loadPrograms(); loadExtras(); }, [loadChallenges, loadPrograms, loadExtras]);

  const claimChallenge = async (ch) => {
    setClaiming(ch.id);
    try {
      const r = await api.post("/challenges/claim", { scope: ch.scope, challenge_id: ch.id });
      toast.success(`Claimed: ${rewardText(ch.reward) || ch.title}`);
      if (r.data.certificate_progress?.length) toast.success("Certificate progress advanced");
      await Promise.all([loadChallenges(), loadPrograms(), refresh()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); } finally { setClaiming(""); }
  };

  const claimProgram = async (p) => {
    setClaiming(p.code);
    try {
      await api.post(`/certificates/programs/claim/${p.code}`);
      toast.success(`Certificate earned: ${p.title}`);
      await Promise.all([loadPrograms(), loadExtras(), refresh()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not claim"); } finally { setClaiming(""); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Trophy className="w-6 h-6 gold-text" />Challenges & Certificates</h1>
        <p className="text-[var(--bone-muted)] text-sm">Daily, weekly and monthly challenges fuel your XP, tokens, and 9 mastery certificates.</p>
      </div>

      <Tabs defaultValue="challenges">
        <TabsList className="bg-white/[0.04]">
          <TabsTrigger value="challenges" data-testid="tab-challenges">Challenges</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates{programs?.claimable ? <span className="ml-1.5 gold-text">({programs.claimable})</span> : null}</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboards</TabsTrigger>
        </TabsList>

        {/* CHALLENGES */}
        <TabsContent value="challenges" className="mt-4 space-y-6">
          {!overview ? <Skeleton className="h-64 w-full bg-white/[0.04]" /> : (
            <>
              <Section icon={Star} title="Monthly Master" subtitle={overview.keys.month} testid="monthly-section">
                <div className="md:max-w-md">
                  <div className="litra-card p-5" style={{ borderColor: "rgba(176,141,60,0.35)" }} data-testid="monthly-card">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display font-semibold">{overview.monthly.title}</div>
                        <p className="text-xs text-[var(--bone-faint)] mt-1">{overview.monthly.description}</p>
                      </div>
                      <Badge className="gold-edge gold-text bg-transparent text-[10px] shrink-0 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Master</Badge>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1"><span className="text-[var(--bone-faint)]">{overview.monthly.progress} / {overview.monthly.target}</span></div>
                      <Progress value={Math.round((overview.monthly.progress / Math.max(1, overview.monthly.target)) * 100)} className="h-2 bg-white/[0.06]" data-testid="monthly-progress" />
                    </div>
                    <div className="mt-2 text-[11px] text-[var(--bone-faint)]">Reward: <span className="gold-text">{rewardText(overview.monthly.reward)}</span></div>
                    <div className="mt-3">
                      {overview.monthly.claimed ? (
                        <div className="flex items-center gap-1.5 text-xs green-text" data-testid="monthly-claimed"><CheckCircle2 className="w-3.5 h-3.5" /> Claimed this month</div>
                      ) : overview.monthly.completed ? (
                        <Button size="sm" onClick={() => claimChallenge(overview.monthly)} disabled={claiming === overview.monthly.id} className="gold-edge gold-text bg-transparent hover:bg-[rgba(176,141,60,0.14)]" data-testid="monthly-claim"><Award className="w-3.5 h-3.5 mr-1.5" /> Claim Master reward</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="glass" onClick={() => navigate("/decode")} data-testid="monthly-go"><ScanText className="w-3.5 h-3.5 mr-1.5" /> Decode</Button>
                      )}
                    </div>
                  </div>
                </div>
              </Section>

              <Section icon={CalendarDays} title="Daily Challenges" subtitle={overview.keys.day} testid="daily-section">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {overview.daily.map((ch) => <ChallengeCard key={ch.id} ch={ch} onClaim={claimChallenge} claiming={claiming === ch.id} onDecode={() => navigate(ch.metric === "lessons" ? "/doctrine" : ch.metric === "studio" ? "/studio" : "/decode")} />)}
                </div>
              </Section>

              <Section icon={CalendarRange} title="Weekly Challenges" subtitle={overview.keys.week} testid="weekly-section">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {overview.weekly.map((ch) => <ChallengeCard key={ch.id} ch={ch} onClaim={claimChallenge} claiming={claiming === ch.id} onDecode={() => navigate("/decode")} />)}
                </div>
              </Section>
            </>
          )}
        </TabsContent>

        {/* CERTIFICATES */}
        <TabsContent value="certificates" className="mt-4 space-y-6">
          {!programs ? <Skeleton className="h-64 w-full bg-white/[0.04]" /> : (
            <Section icon={GraduationCap} title="Certificate Programs" subtitle={`${programs.earned}/9 earned`} testid="programs-section">
              <div className="grid md:grid-cols-2 gap-3" data-testid="programs-grid">
                {programs.programs.map((p) => <ProgramCard key={p.code} p={p} onClaim={claimProgram} claiming={claiming === p.code} />)}
              </div>
            </Section>
          )}

          {certs?.certificates?.length > 0 && (
            <Section icon={Award} title="Milestone Certificates" testid="milestones-section">
              <div className="grid sm:grid-cols-2 gap-4" data-testid="certificates-grid">
                {certs.certificates.map((c) => <CertificateCard key={c.id} certificate={c} username={user?.username} />)}
              </div>
            </Section>
          )}
        </TabsContent>

        {/* LEADERBOARD */}
        <TabsContent value="leaderboard" className="mt-4">
          {!board ? <Skeleton className="h-64 w-full bg-white/[0.04]" /> : (
            <div className="grid md:grid-cols-2 gap-4">
              <LeaderList title="Top by XP" icon={Crown} rows={board.xp} unit="XP" testid="leaderboard-xp" />
              <LeaderList title="Longest Streaks" icon={Flame} rows={board.streak} unit="days" testid="leaderboard-streak" />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const LeaderList = ({ title, icon: Icon, rows, unit, testid }) => (
  <div className="litra-card p-4" data-testid={testid}>
    <div className="text-sm font-display font-semibold flex items-center gap-2 mb-3"><Icon className="w-4 h-4 gold-text" />{title}</div>
    <div className="space-y-1.5">
      {rows?.length ? rows.slice(0, 10).map((r) => (
        <div key={`${r.position}-${r.username}`} className={`flex items-center justify-between px-2.5 py-2 rounded-lg ${r.is_me ? "bg-[rgba(20,90,67,0.18)] border border-[rgba(20,90,67,0.4)]" : "bg-white/[0.02]"}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className={`mono-chip w-5 text-center ${r.position <= 3 ? "gold-text" : "text-[var(--bone-faint)]"}`}>{r.position}</span>
            <span className="text-sm truncate">{r.username}{r.is_me ? " (you)" : ""}</span>
            {r.rank && <span className="text-[10px] text-[var(--bone-faint)] hidden sm:inline">{r.rank}</span>}
          </div>
          <span className="text-sm green-text">{r.value} {unit}</span>
        </div>
      )) : <div className="text-xs text-[var(--bone-faint)]">No data yet.</div>}
    </div>
  </div>
);
